import React, { useState } from 'react';
import ParentCardNoCol from '../../common/ParentCardNoCol';
import { drapcodeIcon } from '../../../common/custom/icon-library';
import { convertTimeStampToDate } from '../../../common/utils/dateUtils';
import showAlert from '../../../common/utils/alert';
import { snakeCaseToTitleCase } from '../../../common/utils/api_utils';
import { generateUrl } from '../../../common/utils/environments';
import voca from 'voca';
import CopyToClipboard from 'react-copy-to-clipboard';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { CustomModal } from '../../../common/ui/CustomModal';
import TooltipInfo from '../../../common/docs/TooltipInfo';
import { description } from '../../../common/utils/event_constants';

export const CustomDataMappingTable = ({
  projectId,
  version,
  customDataMappings,
  environments,
  projectDetail,
  actions,
  flag,
  isReadOnly,
}) => {
  const [toggle, setToggle] = useState(false);
  const [currentMappingId, setCurrentMappingId] = useState('');
  const baseUrl = `https://{{APPLICATION_URL}}/custom-data-mapping/${currentMappingId}`;
  const clipboardStyle = {
    cursor: 'pointer',
    padding: '4px',
    verticalAlign: 'middle',
    margin: 0,
  };

  const copyToClipboard = () => {
    if (baseUrl) toast.success('Copied to clipboard');
  };

  const showPopUp = (uuid) => {
    setCurrentMappingId(uuid);
    setToggle(true);
  };

  const handleClone = (customDataMapping) => {
    const preConfirm = async () => {
      await actions.cloneCustomDataMapping({
        projectId,
        customDataMapping,
        version,
      });
    };
    showAlert(
      `Do you want to clone ${customDataMapping.name} CustomDataMapping ?`,
      preConfirm,
      'Yes, clone it!',
    );
  };

  const handleDelete = (uuid, version) => {
    const preConfirm = () => actions.deleteCustomDataMapping({ projectId, uuid, version });
    showAlert("You won't be able to revert it!", preConfirm, 'Yes, delete it!');
  };

  const generateUrlRow = (env) => {
    const url = `${generateUrl(projectDetail, env.envType)}custom-data-mapping/${currentMappingId}`;
    return (
      <tr key={env.uuid}>
        <td>{snakeCaseToTitleCase(env.envType)} Environment</td>
        <td>
          <p>
            <a
              className="icon-link d-flex justify-content-between align-items-center"
              style={{ color: '#1F2041' }}
              href={url}
              target="_blank"
            >
              {url}
            </a>
          </p>
        </td>
      </tr>
    );
  };

  const generateMappingRow = () => {
    if (customDataMappings && customDataMappings.length) {
      return customDataMappings.map((customDataMapping) => (
        <tr key={customDataMapping.uuid} className="text-left td-align-middle">
          <td>{customDataMapping.name}</td>
          <td>{snakeCaseToTitleCase(customDataMapping.type)}</td>
          <td>{convertTimeStampToDate(customDataMapping.createdAt)}</td>
          <td className="text-right">
            <button
              type="button"
              className="btn btn-theme-submit btn-xs"
              style={{ cursor: 'pointer' }}
              title="View"
              onClick={() => showPopUp(customDataMapping.uuid)}
            >
              <i className="wb webconnect-eye" />
            </button>
            &nbsp;
            <Link
              className="btn btn-xs btn-primary"
              title={`Edit ${customDataMapping.name}`}
              to={{
                pathname: `/custom-component/${version}/v/${projectId}/customDataMapping/edit/${customDataMapping.uuid}`,
              }}
            >
              <i className="wb webconnect-pencil"></i>
            </Link>
            &nbsp;
            <button
              className="btn btn-xs btn-theme-submit"
              onClick={() => (isReadOnly ? undefined : handleClone(customDataMapping))}
              title={isReadOnly ? 'Read-Only Mode' : `Clone ${customDataMapping.name}`}
              disabled={isReadOnly}
            >
              <i className="wb webconnect-clone"></i>
            </button>
            &nbsp;
            <button
              className="btn btn-xs btn-theme-delete"
              onClick={() =>
                flag && !isReadOnly ? handleDelete(customDataMapping.uuid, version) : undefined
              }
              style={{ cursor: flag && !isReadOnly ? 'pointer' : 'not-allowed' }}
              title={isReadOnly ? 'Read-Only Mode' : `Delete ${customDataMapping.name}`}
              disabled={isReadOnly}
            >
              <i className="wb webconnect-trash-solid"></i>
              {flag ? null : (
                <>
                  &nbsp;
                  <TooltipInfo
                    title={description}
                    headerText={`Permission Disabled!`}
                    showHeader={true}
                    showArrow={true}
                  />
                </>
              )}
            </button>
          </td>
        </tr>
      ));
    } else {
      return (
        <tr>
          <td colSpan="4" className="align-middle text-center">
            No Custom Mapping!
          </td>
        </tr>
      );
    }
  };

  return (
    <>
      <ParentCardNoCol
        title="Custom Data Mapping"
        icon="customDataMapping"
        bgColor="drapcode-violet"
        CtaComponent={
          <Link
            className={`btn btn-theme-submit btn-md ${isReadOnly ? 'disabled' : ''}`}
            title={isReadOnly ? 'Read-Only Mode' : 'Create Custom Data Mapping'}
            to={{
              pathname: isReadOnly
                ? '#'
                : `/custom-component/${version}/v/${projectId}/customDataMapping/create`,
            }}
            onClick={(e) => {
              if (isReadOnly) {
                e.preventDefault();
              }
            }}
          >
            <i className={`wb ${drapcodeIcon('add')}`}></i> &nbsp;Create Custom Mapping
          </Link>
        }
      >
        <div className="table-responsive">
          <table className="table table-bordered table-hover table-sm table-word-break connector-list">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Created At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>{generateMappingRow()}</tbody>
          </table>
        </div>
      </ParentCardNoCol>
      <CustomModal
        isOpen={toggle}
        toggle={() => setToggle(!toggle)}
        size="lg"
        header={
          <div
            className="d-flex justify-content-between align-items-center"
            style={{ width: '45rem' }}
          >
            <strong>Custom Data Mapping Url</strong>
          </div>
        }
        body={
          <>
            <strong>Base Url: </strong>&nbsp;
            <CopyToClipboard
              text={baseUrl}
              onCopy={() => copyToClipboard(baseUrl)}
              title={'Copy to clipboard'}
              style={clipboardStyle}
            >
              <span>
                {voca.first(baseUrl, 40)}...
                {voca.last(baseUrl, 30)} <i className="webconnect webconnect-files" />
              </span>
            </CopyToClipboard>
            <div className="row pt-2 pb-2">
              <div className="col-md-12 col-sm-12">
                <table className="table table-stripped table-bordered table-hover">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>URL</th>
                    </tr>
                  </thead>
                  {environments ? (
                    <tbody>{environments.map((env) => generateUrlRow(env))}</tbody>
                  ) : (
                    'No Environment'
                  )}
                </table>
              </div>
            </div>
          </>
        }
      />
    </>
  );
};
